import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

import { EXEC_FETCH_INTERVAL_TIME, THRESHOLD_OF_TIMEOUT } from '../../utils/constants';
import { apiGetAsoko2jobStatus } from '../../utils/api';

/**
 * サイドバー常駐の実行状態メッセージ
 *
 * 要件：
 *  - executing 中はポーリング継続（画面遷移しても継続）
 *  - done になったらメッセージ表示（exec_is_done / exec_has_error）
 *  - 実行画面から抜けて→実行画面へ再入場したタイミングで「完了メッセージ」を消す
 *    （ただし executing 中に戻った場合は消さない）
 */
const MessageOfExecuteDone = (props) => {
  const {
    isExecProgress,        // 使わなくてもOK（表示制御したければ利用）
    setIsExecProgress,
    execStatus,
    setExecStatus,
  } = props;

  const { t } = useTranslation();
  const location = useLocation();

  const [message, setMessage] = useState('');

  const intervalId = useRef(null);
  const inFlight = useRef(false);
  const prevPathRef = useRef(location.pathname);

  // --- ユーティリティ：実行画面パス判定（あなたの Routes に合わせた） ---
  const isExecPagePath = (pathname) => {
    // /picking/newexec, /pickinggeneral/newexec, /simulation/newexec
    return /\/(picking|pickinggeneral|simulation)\/newexec$/.test(pathname);
  };

  // --- 実行コンテキストをクリア（doneのときに残す/消すを分ける） ---
  const clearExecContextForDone = () => {
    // done判定は残したい（戻ってきた時にクリアするトリガーになる）
    // なので exec_status は残す。
    Cookies.remove('job_id');
    Cookies.remove('exec_time');
    Cookies.remove('exec_warehouse_id');
    // kind は必要なら残してもOK。ここでは残す/消すは好み。
    // Cookies.remove('kind');
    setIsExecProgress(false);
  };

  const clearAllExecContext = () => {
    Cookies.remove('job_id');
    Cookies.remove('exec_status');
    Cookies.remove('exec_time');
    Cookies.remove('exec_warehouse_id');
    Cookies.remove('kind');
    setIsExecProgress(false);
    setExecStatus('');
    setMessage('');
  };

  // --- 画面遷移を監視し、実行画面へ再入場したら「完了メッセージ」を消す ---
  useEffect(() => {
    const prev = prevPathRef.current;
    const curr = location.pathname;
    prevPathRef.current = curr;

    // 画面が変わっていないなら何もしない
    if (prev === curr) return;

    // 実行画面へ入ったか？
    if (!isExecPagePath(curr)) return;

    // 完了済みのときだけ消す（executing 中は消さない）
    const exec_status_cookie = Cookies.get('exec_status');
    if (exec_status_cookie === 'done') {
      // ✅ ここで「完了メッセージ」を消して、次の実行前の初期状態へ
      setMessage('');
      setExecStatus('');
      clearAllExecContext();
    }
  }, [location.pathname]);

  // --- ポーリング本体：ジョブ状態を取ってメッセージ更新 ---
  const fetchExecIsDone = async (job_id) => {
    try {
      const resp = await apiGetAsoko2jobStatus(job_id);
      const st = resp?.data?.data ?? resp?.data; // jsend/非jsend両対応（保険）

      // st = { done:boolean, ok:boolean, status:string, message?:string ... } の想定
      if (!st?.done) {
        setMessage('exec_in_progress');
        return;
      }

      // 完了
      if (st.ok) {
        setMessage('exec_is_done');
      } else {
        setMessage('exec_has_error');
      }

      // 完了フラグは Cookie に残す（再入場時のクリアトリガー）
      Cookies.set('exec_status', 'done');

      // 実行コンテキスト（job_idなど）は消してOK（ポーリング停止になる）
      clearExecContextForDone();

    } catch (error) {
      // ここで throw しない（以降の処理が死ぬので）
      console.log('[poll] error=', error);
      console.log('[poll] error status=', error?.response?.status);
      console.log('[poll] url=', (error?.config?.baseURL || '') + (error?.config?.url || ''));
      console.log('[poll] response=', error?.response?.data);

      setMessage('exec_has_error');
      Cookies.set('exec_status', 'done');
      clearExecContextForDone();
    }
  };

  const checkExecProgress = async () => {
    if (inFlight.current) return;
    inFlight.current = true;

    try {
      const currentTime = Date.now();
      const execTime = Number(Cookies.get('exec_time') || 0);
      const currentJobId = Cookies.get('job_id');
      const currentExecStatus = Cookies.get('exec_status');
      const token = Cookies.get('token');

      const isLogOut = token === 'null' || token == null;
      const timeDifference = execTime ? currentTime - execTime : 0;
      const isTimeOut = execTime ? timeDifference > THRESHOLD_OF_TIMEOUT : false;

      const isJobIdExist = !!currentJobId && currentJobId !== 'undefined';
      const isExecuting = currentExecStatus === 'executing';

      // ログアウト
      if (isLogOut) {
        clearAllExecContext();
        return;
      }

      // タイムアウト → エラー扱いで完了
      if (isTimeOut) {
        setMessage('exec_has_error');
        Cookies.set('exec_status', 'done');
        clearExecContextForDone();
        return;
      }

      // 実行中かつ job_id があるときだけポーリング
      if (isExecuting && isJobIdExist) {
        await fetchExecIsDone(currentJobId);
      }

      // 条件が揃ってないときは何もしない（ここで reset しない）
      return;

    } finally {
      inFlight.current = false;
    }
  };

  // --- interval は常駐コンポーネントなので「止めない」方針が安全 ---
  // ※止めると次回の実行時に再開しなくなる（このコンポーネントはunmountされないため）
  useEffect(() => {
    intervalId.current = setInterval(checkExecProgress, EXEC_FETCH_INTERVAL_TIME);
    return () => {
      if (intervalId.current) clearInterval(intervalId.current);
      intervalId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // execStatus（親state）が done になったときの扱い（必要なら）
  // ここでは「親から done が飛んできても、Cookie done を維持」するだけ
  useEffect(() => {
    if (execStatus === 'done') {
      Cookies.set('exec_status', 'done');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execStatus]);

  const color = message === 'exec_has_error' ? 'red' : 'black';

  // message が空なら何も出さない（表示が残っているように見えるのを防ぐ）
  if (!message) return null;

  return (
    <div style={{ position: 'absolute', bottom: '40px', left: '10px' }}>
      <div style={{ color }}>
        <Trans t={t} i18nKey={message} />
      </div>
    </div>
  );
};

export default MessageOfExecuteDone;